import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Client } from "colyseus.js";

const ClientColyseusContext = createContext();

export const useClientColyseus = () => useContext(ClientColyseusContext);

export const ClientColyseusProvider = ({ children }) => {
  const [roomsList, setRoomsList] = useState([]);
  const [room, setRoom] = useState(null);
  const [client, setClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const clientRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoaded, setCoursesLoaded] = useState(false);

  useEffect(() => {
    const url = process.env.REACT_APP_API_URL;
    const colyseusClient = new Client(url);
    clientRef.current = colyseusClient;
    setClient(colyseusClient);
    setSessionId(colyseusClient.id); // Save own sessionId for host checks
    getAvailableRooms();
    return () => {
      if (room) room.leave();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!coursesLoaded) {
      fetch(process.env.REACT_APP_API_URL + "/course")
        .then((res) => res.json())
        .then((data) => {
          setCourses(data.courses || []);
          setCoursesLoaded(true);
        })
        .catch((err) => {
          console.error("Failed to fetch courses in context:", err);
        });
    }
  }, [coursesLoaded]);

  const getAvailableRooms = useCallback(async () => {
    if (!clientRef.current) return;
    try {
      // Try multiple room types that might be registered on the backend
      let rooms = [];
      const roomTypes = ["SpeedMod", "speed_duel", "game_room", "quiz_room"];

      for (const roomType of roomTypes) {
        try {
          const typeRooms = await clientRef.current.getAvailableRooms(roomType);
          rooms = rooms.concat(typeRooms);
        } catch (err) {
          // Continue trying other room types
          continue;
        }
      }

      setRoomsList(rooms);
    } catch (err) {
      setRoomsList([]);
    }
  }, []);

  const createRoom = useCallback(
    async (
      mod,
      name,
      pwd,
      course,
      course_id,
      nbQuestions,
      mode = "competitive",
      playerOptions = {}
    ) => {
      if (!clientRef.current) return;
      console.log("Creating room with options:", {
        mod,
        name,
        pwd,
        course,
        course_id,
        nbQuestions,
        mode,
        playerOptions,
      });

      try {
        console.log("Attempting to create SpeedMod room");

        const roomOptions = {
          roomName: name,
          roomPwd: pwd,
          course,
          course_id,
          nbQuestions: parseInt(nbQuestions),
          mod: mod,
          mode: mode,
          maxPlayers: 8,
          // Force unique room creation by adding timestamp and random ID
          uniqueId: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          forceCreate: true,
        };

        console.log("Room creation options:", roomOptions);

        // Create room and immediately join it
        const newRoom = await clientRef.current.create(
          "speed_duel",
          roomOptions
        );

        console.log("Room created successfully:", newRoom.id);
        console.log("Room state:", newRoom.state);

        // Verify we created a new room, not joined existing
        if (newRoom && newRoom.id) {
          console.log("Setting up new room with ID:", newRoom.id);
          console.log("New room initial state:", newRoom.state);
          setupRoom(newRoom);

          // Send player name after joining
          if (playerOptions.name) {
            console.log("Setting player name:", playerOptions.name);
            // Wait a moment for room to be fully set up
            setTimeout(() => {
              if (newRoom && newRoom.send) {
                newRoom.send("set_name", { name: playerOptions.name });
              }
            }, 100);
          }

          getAvailableRooms(); // Refresh the room list
          return newRoom; // Return the room for verification
        } else {
          throw new Error("Room creation returned invalid room object");
        }
      } catch (err) {
        console.error("Failed to create room:", err);
        console.error("Error details:", err.message, err.code);

        const errorMsg = `Failed to create room: ${err.message}. Please try again.`;
        console.error(errorMsg);
        alert(errorMsg);
        throw new Error(errorMsg);
      }
    },
    [getAvailableRooms]
  );

  const joinById = useCallback(async (roomId, options = {}) => {
    if (!clientRef.current) throw new Error("Multiplayer client not initialized.");
    try {
      const joinedRoom = await clientRef.current.joinById(roomId, options);
      setupRoom(joinedRoom);
    } catch (err) {
      throw new Error("Failed to join room: " + (err?.message || "Unknown error"));
    }
  }, []);

  const setupRoom = useCallback(
    (roomInstance) => {
      console.log("setupRoom called with:", roomInstance.id);
      console.log("setupRoom - roomInstance.state:", roomInstance.state);

      // Clear any existing room state first
      if (room && room.id !== roomInstance.id) {
        console.log("Leaving previous room:", room.id);
        room.leave();
      }

      setRoom(roomInstance);
      setMessages([]);
      setGameState({ ...roomInstance.state });
      setSessionId(roomInstance.sessionId);

      const checkIsMaster = (state) => {
        if (!state || !state.master_client || !roomInstance.sessionId) {
          setIsMaster(false);
          return;
        }
        const isMasterClient = state.master_client === roomInstance.sessionId;
        if (isMaster !== isMasterClient) {
          setIsMaster(isMasterClient);
          console.log(
            `User ${isMasterClient ? "is" : "is not"} master. Master: ${
              state.master_client
            }, User: ${roomInstance.sessionId}`
          );
        }
      };

      checkIsMaster(roomInstance.state);

      roomInstance.onStateChange((state) => {
        console.log("Room state changed:", state);
        setGameState({ ...state });
        checkIsMaster(state);
      });

      roomInstance.onMessage("host_changed", (msg) => {
        checkIsMaster(roomInstance.state);
      });
      roomInstance.onMessage("chat", (msg) => {
        setMessages((prev) => [...prev, msg]);
      });

      roomInstance.onMessage("joined", (message) => {
        console.log("Successfully joined room:", message);
      });

      roomInstance.onMessage("messages", (message) => {
        console.log("Received message history:", message);
        setMessages((prev) => [
          ...prev,
          ...(Array.isArray(message) ? message : [message]),
        ]);
      });

      roomInstance.onMessage("player_joined", (player) => {
        console.log("Player joined:", player);
      });

      roomInstance.onMessage("finish", (message) => {
        console.log("Game finished:", message);
        // Optional: handle game finish, e.g., show a summary screen
      });
      roomInstance.onLeave(() => {
        setRoom(null);
        setIsMaster(false);
        setMessages([]);
        setGameState(null);
        setSessionId(null);
      });
    },
    [room]
  );

  const sendChat = useCallback(
    (msg) => {
      if (room) room.send("chat", msg);
    },
    [room]
  );

  const value = useMemo(
    () => ({
      client,
      roomsList,
      room,
      isMaster,
      gameState,
      messages,
      sessionId,
      courses,
      coursesLoaded,
      getAvailableRooms,
      createRoom,
      joinById,
      sendChat,
    }),
    [
      client,
      roomsList,
      room,
      isMaster,
      gameState,
      messages,
      sessionId,
      courses,
      coursesLoaded,
      getAvailableRooms,
      createRoom,
      joinById,
      sendChat,
    ]
  );

  return (
    <ClientColyseusContext.Provider value={value}>
      {children}
    </ClientColyseusContext.Provider>
  );
};

export default ClientColyseusContext;
